import { useCallback, useEffect, useState } from "react";
import { parseSessionRows, type Session as SessionT } from "@heraion/shared";

import { apiFetch, apiFetchConditional, ApiError } from "./api";
import { lsCache } from "./lsCache";

// Workouts history list data for the web-rnw console. Mirrors the mobile
// useSessionsListData lifecycle minus the offline/outbox machinery the
// console doesn't have: cache-first SWR (localStorage, so a cold load shows
// the previous window before the network lands — [[reference_web_rnw_needs_swr_cache]])
// + cursor pagination over GET /api/sessions + conditional (ETag /
// If-None-Match) refresh on the first page, mirroring mobile's
// useSessionsListData.ts.

const PAGE_SIZE = 50;

type Page = {
  items: SessionT[];
  cursor: string | null;
  // Strong ETag of the first-page response these items came from, sent back
  // as If-None-Match on the next mount so the server can answer 304 "still
  // current" — mirrors apps/mobile/src/screens/useSessionsListData.ts.
  etag?: string | null;
};

// First-page cache only (later pages are cheap on-demand cursor walks),
// matching the mobile cache shape so the two stay conceptually aligned.
const firstPageCache = lsCache<Page>("workouts/firstPage");

export type SessionsList = {
  items: SessionT[] | null;
  cursor: string | null;
  error: string | null;
  refreshing: boolean;
  loadingMore: boolean;
  onRefresh: () => void;
  onEndReached: () => void;
};

export function useSessionsList(): SessionsList {
  const [items, setItems] = useState<SessionT[] | null>(null);
  const [cursor, setCursor] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);

  // `next === null` = first page: conditional GET (If-None-Match from the
  // cached ETag) so an unchanged window answers 304 and skips the re-render.
  // `next !== null` = cursor page: plain GET — each page is a distinct URL
  // the user only reaches by scrolling, so If-None-Match buys nothing.
  const fetchPage = useCallback(
    async (
      next: string | null,
      opts: { ifNoneMatch?: string | null } = {},
    ): Promise<Page & { notModified: boolean }> => {
      if (next !== null) {
        const res = await apiFetch<{ items?: unknown[]; cursor?: string | null }>(
          `/api/sessions?limit=${PAGE_SIZE}&cursor=${encodeURIComponent(next)}`,
        );
        return {
          items: parseSessionRows(res.items ?? []),
          cursor: res.cursor ?? null,
          notModified: false,
        };
      }
      const res = await apiFetchConditional<{
        items?: unknown[];
        cursor?: string | null;
      }>(`/api/sessions?limit=${PAGE_SIZE}`, {
        ifNoneMatch: opts.ifNoneMatch ?? null,
      });
      if (res.status === 304) {
        return { items: [], cursor: null, notModified: true };
      }
      return {
        items: parseSessionRows(res.data.items ?? []),
        cursor: res.data.cursor ?? null,
        etag: res.etag,
        notModified: false,
      };
    },
    [],
  );

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cached = await firstPageCache.read();
      if (!cancelled && cached !== null) {
        setItems(cached.items);
        setCursor(cached.cursor);
      }
      try {
        // Pass the cached ETag: if the server's first page is unchanged it
        // answers 304 and we keep the rows we just hydrated.
        const page = await fetchPage(null, { ifNoneMatch: cached?.etag ?? null });
        if (cancelled || page.notModified) return;
        setItems(page.items);
        setCursor(page.cursor);
        void firstPageCache.write({
          items: page.items,
          cursor: page.cursor,
          etag: page.etag,
        });
      } catch (err) {
        if (cancelled || cached !== null) return; // keep stale on error
        setError(`api_error_${(err as ApiError).status ?? 0}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [fetchPage]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setError(null);
    void (async () => {
      try {
        const page = await fetchPage(null);
        setItems(page.items);
        setCursor(page.cursor);
        void firstPageCache.write({
          items: page.items,
          cursor: page.cursor,
          etag: page.etag,
        });
      } catch (err) {
        setError(`api_error_${(err as ApiError).status ?? 0}`);
      } finally {
        setRefreshing(false);
      }
    })();
  }, [fetchPage]);

  const onEndReached = useCallback(() => {
    if (cursor === null || loadingMore || refreshing) return;
    setLoadingMore(true);
    void (async () => {
      try {
        const page = await fetchPage(cursor);
        setItems((prev) => [...(prev ?? []), ...page.items]);
        setCursor(page.cursor);
      } catch (err) {
        setError(`api_error_${(err as ApiError).status ?? 0}`);
      } finally {
        setLoadingMore(false);
      }
    })();
  }, [cursor, loadingMore, refreshing, fetchPage]);

  return {
    items,
    cursor,
    error,
    refreshing,
    loadingMore,
    onRefresh,
    onEndReached,
  };
}
